/**
 * Campaign Management Page
 * 
 * Kampanya yönetimi - CRUD, agent atama, kontrol
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Tabs,
    Tab,
    Tooltip,
    Alert,
    CircularProgress,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Card,
    CardContent,
    LinearProgress,
    Switch,
    FormControlLabel,
    Slider
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PlayArrow as PlayIcon,
    Pause as PauseIcon,
    Stop as StopIcon,
    Refresh as RefreshIcon,
    ContentCopy as CloneIcon,
    People as PeopleIcon,
    Settings as SettingsIcon,
    Assessment as StatsIcon,
    ListAlt as ListIcon,
    Phone as PhoneIcon,
    Schedule as ScheduleIcon,
    Queue as QueueIcon
} from '@mui/icons-material';
import campaignService, { Campaign, Agent, CampaignStatus } from '../services/campaignService';
import dialerListService, { DialerList } from '../services/dialerListService';
import { queueAPI } from '../services/api';

interface Queue {
    _id: string;
    name: string;
    assignedAgentIds?: Array<{ _id: string; name: string }>;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
        </div>
    );
}

const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
    draft: 'default',
    scheduled: 'info',
    running: 'success',
    paused: 'warning',
    completed: 'secondary',
    archived: 'error'
};

const statusLabels: Record<string, string> = {
    draft: 'Taslak',
    scheduled: 'Zamanlanmış',
    running: 'Çalışıyor',
    paused: 'Duraklatılmış',
    completed: 'Tamamlandı',
    archived: 'Arşivlendi'
};

export default function CampaignManagement() {
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    // Dialog states
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [agentDialogOpen, setAgentDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    
    const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
    const [availableAgents, setAvailableAgents] = useState<Agent[]>([]);
    const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
    const [campaignLists, setCampaignLists] = useState<DialerList[]>([]);
    const [campaignStatus, setCampaignStatus] = useState<CampaignStatus | null>(null);
    
    // Queue state
    const [queues, setQueues] = useState<Queue[]>([]);
    const [selectedQueueId, setSelectedQueueId] = useState<string>('');
    
    // CSV Import state
    const [csvDialogOpen, setCsvDialogOpen] = useState(false);
    const [csvFile, setCsvFile] = useState<File | null>(null);
    const [csvPreview, setCsvPreview] = useState<string[][]>([]);
    const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
    const [csvMapping, setCsvMapping] = useState<Record<string, string>>({});
    const [csvListName, setCsvListName] = useState('');
    const [csvUploading, setCsvUploading] = useState(false);
    const [csvStep, setCsvStep] = useState<'upload' | 'mapping' | 'confirm'>('upload');
    
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        type: 'predictive' as Campaign['type'],
        queueId: '',
        callerIdNumber: '',
        callerIdName: '',
        dialerSettings: {
            maxConcurrentCalls: 10,
            targetAgentWaitTime: 5,
            maxDialRatio: 3.0,
            minDialRatio: 1.0,
            maxAbandonRate: 0.03,
            ringTimeout: 25,
            retryAttempts: 3,
            retryDelayMinutes: 30
        },
        workingHours: {
            enabled: true,
            timezone: 'Europe/Istanbul',
            days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
            timeSlots: [{ startTime: '09:00', endTime: '18:00' }]
        }
    });
    
    const [tabValue, setTabValue] = useState(0);
    
    // Load campaigns
    const loadCampaigns = useCallback(async () => {
        try {
            setLoading(true);
            const data = await campaignService.getCampaigns();
            setCampaigns(data);
            setError(null);
        } catch (err: any) {
            setError(err.message || 'Kampanyalar yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    }, []);
    
    // Load queues
    const loadQueues = useCallback(async () => {
        try {
            const response = await queueAPI.getAll();
            setQueues(response.data.queues || response.data || []);
        } catch (err: any) {
            console.error('Kuyruklar yüklenirken hata:', err);
        }
    }, []);
    
    useEffect(() => {
        loadCampaigns();
        loadQueues();
    }, [loadCampaigns, loadQueues]);
    
    // CSV file handling
    const handleCsvFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        setCsvFile(file);
        setCsvListName(file.name.replace('.csv', ''));
        
        // Parse CSV to get headers and preview
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            const lines = text.split('\n').filter(line => line.trim());
            
            if (lines.length > 0) {
                // First line is headers
                const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
                setCsvHeaders(headers);
                
                // Auto-map common fields
                const autoMapping: Record<string, string> = {};
                headers.forEach(header => {
                    const lower = header.toLowerCase();
                    if (lower.includes('phone') || lower.includes('telefon') || lower.includes('numara')) {
                        autoMapping[header] = 'phoneNumber';
                    } else if (lower.includes('firstname') || lower.includes('first_name') || lower === 'ad' || lower === 'isim') {
                        autoMapping[header] = 'firstName';
                    } else if (lower.includes('lastname') || lower.includes('last_name') || lower === 'soyad') {
                        autoMapping[header] = 'lastName';
                    } else if (lower.includes('company') || lower.includes('firma') || lower.includes('şirket')) {
                        autoMapping[header] = 'company';
                    } else if (lower.includes('email') || lower.includes('eposta') || lower.includes('mail')) {
                        autoMapping[header] = 'email';
                    }
                });
                setCsvMapping(autoMapping);
                
                // Preview first 5 data rows
                const previewData = lines.slice(1, 6).map(line => 
                    line.split(',').map(cell => cell.trim().replace(/"/g, ''))
                );
                setCsvPreview(previewData);
                
                setCsvStep('mapping');
            }
        };
        reader.readAsText(file);
    };
    
    const handleCsvUpload = async () => {
        if (!csvFile || !selectedCampaign) {
            setError('Kampanya seçilmeli');
            return;
        }
        
        // Check if phone number column is mapped
        const phoneNumberMapping = Object.entries(csvMapping).find(([_, field]) => field === 'phoneNumber');
        if (!phoneNumberMapping) {
            setError('Telefon numarası sütunu eşleştirilmeli');
            return;
        }
        
        setCsvUploading(true);
        try {
            // First create a list
            const newList = await dialerListService.createList({
                name: csvListName || csvFile.name.replace('.csv', ''),
                campaignId: selectedCampaign._id,
                description: `CSV'den içe aktarıldı: ${csvFile.name}`
            });
            
            // Build field mapping array
            const fieldMapping = Object.entries(csvMapping).map(([csvColumn, systemField]) => ({
                csvColumn,
                systemField
            }));
            
            // Import CSV to the list
            await dialerListService.importCsv(newList._id, csvFile, fieldMapping);
            
            // Refresh campaign details
            const details = await campaignService.getCampaign(selectedCampaign._id);
            setCampaignLists(details.lists);
            
            // Close dialog and reset
            resetCsvDialog();
            
            setError(null);
        } catch (err: any) {
            setError(err.message || 'CSV yüklenirken hata oluştu');
        } finally {
            setCsvUploading(false);
        }
    };
    
    const resetCsvDialog = () => {
        setCsvDialogOpen(false);
        setCsvFile(null);
        setCsvHeaders([]);
        setCsvPreview([]);
        setCsvMapping({});
        setCsvListName('');
        setCsvStep('upload');
    };
    
    // Load available agents
    const loadAvailableAgents = async () => {
        try {
            const agents = await campaignService.getAvailableAgents();
            setAvailableAgents(agents);
        } catch (err) {
            console.error('Error loading agents:', err);
        }
    };
    
    // Create campaign
    const handleCreate = async () => {
        try {
            await campaignService.createCampaign(formData);
            setCreateDialogOpen(false);
            resetForm();
            loadCampaigns();
        } catch (err: any) {
            setError(err.message);
        }
    };
    
    // Update campaign
    const handleUpdate = async () => {
        if (!selectedCampaign) return;
        try {
            await campaignService.updateCampaign(selectedCampaign._id, formData);
            setEditDialogOpen(false);
            loadCampaigns();
        } catch (err: any) {
            setError(err.message);
        }
    };
    
    // Delete campaign
    const handleDelete = async () => {
        if (!selectedCampaign) return;
        try {
            await campaignService.deleteCampaign(selectedCampaign._id);
            setDeleteConfirmOpen(false);
            setSelectedCampaign(null);
            loadCampaigns();
        } catch (err: any) {
            setError(err.message);
        }
    };
    
    // Clone campaign
    const handleClone = async (campaign: Campaign) => {
        try {
            await campaignService.cloneCampaign(campaign._id, `${campaign.name} (Kopya)`);
            loadCampaigns();
        } catch (err: any) {
            setError(err.message);
        }
    };
    
    // Campaign control
    const handleStart = async (campaign: Campaign) => {
        try {
            await campaignService.startCampaign(campaign._id);
            loadCampaigns();
        } catch (err: any) {
            setError(err.message);
        }
    };
    
    const handlePause = async (campaign: Campaign) => {
        try {
            await campaignService.pauseCampaign(campaign._id);
            loadCampaigns();
        } catch (err: any) {
            setError(err.message);
        }
    };
    
    const handleResume = async (campaign: Campaign) => {
        try {
            await campaignService.resumeCampaign(campaign._id);
            loadCampaigns();
        } catch (err: any) {
            setError(err.message);
        }
    };
    
    const handleStop = async (campaign: Campaign) => {
        try {
            await campaignService.stopCampaign(campaign._id);
            loadCampaigns();
        } catch (err: any) {
            setError(err.message);
        }
    };
    
    // Agent assignment
    const openAgentDialog = async (campaign: Campaign) => {
        setSelectedCampaign(campaign);
        setSelectedAgentIds(campaign.assignedAgentIds?.map(a => a._id) || []);
        await loadAvailableAgents();
        setAgentDialogOpen(true);
    };
    
    const handleAssignAgents = async () => {
        if (!selectedCampaign) return;
        try {
            await campaignService.assignAgents(selectedCampaign._id, selectedAgentIds);
            setAgentDialogOpen(false);
            loadCampaigns();
        } catch (err: any) {
            setError(err.message);
        }
    };
    
    // View details
    const openDetailDialog = async (campaign: Campaign) => {
        setSelectedCampaign(campaign);
        try {
            const [details, status] = await Promise.all([
                campaignService.getCampaign(campaign._id),
                campaignService.getCampaignStatus(campaign._id)
            ]);
            setCampaignLists(details.lists);
            setCampaignStatus(status);
            setDetailDialogOpen(true);
        } catch (err: any) {
            setError(err.message);
        }
    };
    
    // Open edit dialog
    const openEditDialog = (campaign: Campaign) => {
        setSelectedCampaign(campaign);
        setFormData({
            name: campaign.name,
            description: campaign.description || '',
            type: campaign.type,
            queueId: campaign.queueId?._id || '',
            callerIdNumber: campaign.callerIdNumber || '',
            callerIdName: campaign.callerIdName || '',
            dialerSettings: campaign.dialerSettings || formData.dialerSettings,
            workingHours: campaign.workingHours || formData.workingHours
        });
        setEditDialogOpen(true);
    };
    
    // Reset form
    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            type: 'predictive',
            queueId: '',
            callerIdNumber: '',
            callerIdName: '',
            dialerSettings: {
                maxConcurrentCalls: 10,
                targetAgentWaitTime: 5,
                maxDialRatio: 3.0,
                minDialRatio: 1.0,
                maxAbandonRate: 0.03,
                ringTimeout: 25,
                retryAttempts: 3,
                retryDelayMinutes: 30
            },
            workingHours: {
                enabled: true,
                timezone: 'Europe/Istanbul',
                days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
                timeSlots: [{ startTime: '09:00', endTime: '18:00' }]
            }
        });
    };
    
    // Toggle agent selection
    const toggleAgent = (agentId: string) => {
        setSelectedAgentIds(prev =>
            prev.includes(agentId)
                ? prev.filter(id => id !== agentId)
                : [...prev, agentId]
        );
    };
    
    // Render campaign form
    const renderCampaignForm = () => (
        <Box>
            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
                <Tab label="Genel" />
                <Tab label="Dialer Ayarları" />
                <Tab label="Çalışma Saatleri" />
            </Tabs>
            
            <TabPanel value={tabValue} index={0}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Kampanya Adı"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Açıklama"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            multiline
                            rows={2}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                            <InputLabel>Dialer Tipi</InputLabel>
                            <Select
                                value={formData.type}
                                label="Dialer Tipi"
                                onChange={(e) => setFormData({ ...formData, type: e.target.value as Campaign['type'] })}
                            >
                                <MenuItem value="predictive">Predictive</MenuItem>
                                <MenuItem value="power">Power</MenuItem>
                                <MenuItem value="progressive">Progressive</MenuItem>
                                <MenuItem value="preview">Preview</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth required>
                            <InputLabel>Kuyruk *</InputLabel>
                            <Select
                                value={formData.queueId}
                                label="Kuyruk *"
                                onChange={(e) => setFormData({ ...formData, queueId: e.target.value })}
                            >
                                {queues.map(queue => (
                                    <MenuItem key={queue._id} value={queue._id}>
                                        {queue.name} ({queue.assignedAgentIds?.length || 0} agent)
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Arayan Numara (Caller ID)"
                            value={formData.callerIdNumber}
                            onChange={(e) => setFormData({ ...formData, callerIdNumber: e.target.value })}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Arayan İsim"
                            value={formData.callerIdName}
                            onChange={(e) => setFormData({ ...formData, callerIdName: e.target.value })}
                        />
                    </Grid>
                </Grid>
            </TabPanel>
            
            <TabPanel value={tabValue} index={1}>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography gutterBottom>Maks. Eş Zamanlı Çağrı</Typography>
                        <Slider
                            value={formData.dialerSettings.maxConcurrentCalls}
                            onChange={(_, v) => setFormData({
                                ...formData,
                                dialerSettings: { ...formData.dialerSettings, maxConcurrentCalls: v as number }
                            })}
                            min={1}
                            max={50}
                            marks
                            valueLabelDisplay="on"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography gutterBottom>Hedef Agent Bekleme Süresi (sn)</Typography>
                        <Slider
                            value={formData.dialerSettings.targetAgentWaitTime}
                            onChange={(_, v) => setFormData({
                                ...formData,
                                dialerSettings: { ...formData.dialerSettings, targetAgentWaitTime: v as number }
                            })}
                            min={1}
                            max={30}
                            valueLabelDisplay="on"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography gutterBottom>Maks. Dial Ratio</Typography>
                        <Slider
                            value={formData.dialerSettings.maxDialRatio}
                            onChange={(_, v) => setFormData({
                                ...formData,
                                dialerSettings: { ...formData.dialerSettings, maxDialRatio: v as number }
                            })}
                            min={1}
                            max={5}
                            step={0.1}
                            valueLabelDisplay="on"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography gutterBottom>Çaldırma Süresi (sn)</Typography>
                        <Slider
                            value={formData.dialerSettings.ringTimeout}
                            onChange={(_, v) => setFormData({
                                ...formData,
                                dialerSettings: { ...formData.dialerSettings, ringTimeout: v as number }
                            })}
                            min={10}
                            max={60}
                            valueLabelDisplay="on"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography gutterBottom>Tekrar Deneme Sayısı</Typography>
                        <Slider
                            value={formData.dialerSettings.retryAttempts}
                            onChange={(_, v) => setFormData({
                                ...formData,
                                dialerSettings: { ...formData.dialerSettings, retryAttempts: v as number }
                            })}
                            min={0}
                            max={10}
                            valueLabelDisplay="on"
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography gutterBottom>Tekrar Deneme Aralığı (dk)</Typography>
                        <Slider
                            value={formData.dialerSettings.retryDelayMinutes}
                            onChange={(_, v) => setFormData({
                                ...formData,
                                dialerSettings: { ...formData.dialerSettings, retryDelayMinutes: v as number }
                            })}
                            min={5}
                            max={120}
                            valueLabelDisplay="on"
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Typography gutterBottom>Maks. Abandon Rate (%)</Typography>
                        <Slider
                            value={formData.dialerSettings.maxAbandonRate * 100}
                            onChange={(_, v) => setFormData({
                                ...formData,
                                dialerSettings: { ...formData.dialerSettings, maxAbandonRate: (v as number) / 100 }
                            })}
                            min={1}
                            max={10}
                            step={0.5}
                            valueLabelDisplay="on"
                            valueLabelFormat={(v) => `${v}%`}
                        />
                    </Grid>
                </Grid>
            </TabPanel>
            
            <TabPanel value={tabValue} index={2}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.workingHours.enabled}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        workingHours: { ...formData.workingHours, enabled: e.target.checked }
                                    })}
                                />
                            }
                            label="Çalışma Saatlerini Uygula"
                        />
                    </Grid>
                    {formData.workingHours.enabled && (
                        <>
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="subtitle2" gutterBottom>Çalışma Günleri</Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                                        <Chip
                                            key={day}
                                            label={day.charAt(0).toUpperCase() + day.slice(1, 3)}
                                            color={formData.workingHours.days.includes(day) ? 'primary' : 'default'}
                                            onClick={() => {
                                                const days = formData.workingHours.days.includes(day)
                                                    ? formData.workingHours.days.filter(d => d !== day)
                                                    : [...formData.workingHours.days, day];
                                                setFormData({
                                                    ...formData,
                                                    workingHours: { ...formData.workingHours, days }
                                                });
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Başlangıç Saati"
                                    type="time"
                                    value={formData.workingHours.timeSlots[0]?.startTime || '09:00'}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        workingHours: {
                                            ...formData.workingHours,
                                            timeSlots: [{ ...formData.workingHours.timeSlots[0], startTime: e.target.value }]
                                        }
                                    })}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Bitiş Saati"
                                    type="time"
                                    value={formData.workingHours.timeSlots[0]?.endTime || '18:00'}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        workingHours: {
                                            ...formData.workingHours,
                                            timeSlots: [{ ...formData.workingHours.timeSlots[0], endTime: e.target.value }]
                                        }
                                    })}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </TabPanel>
        </Box>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">
                    <PhoneIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Kampanya Yönetimi
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadCampaigns}
                    >
                        Yenile
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            resetForm();
                            setCreateDialogOpen(true);
                        }}
                    >
                        Yeni Kampanya
                    </Button>
                </Box>
            </Box>
            
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Kampanya Adı</TableCell>
                                <TableCell>Tip</TableCell>
                                <TableCell>Durum</TableCell>
                                <TableCell align="center">Kuyruk</TableCell>
                                <TableCell align="center">Kişiler</TableCell>
                                <TableCell align="center">Çağrılar</TableCell>
                                <TableCell align="center">Başarı %</TableCell>
                                <TableCell align="right">İşlemler</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {campaigns.map((campaign) => (
                                <TableRow key={campaign._id} hover>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="subtitle2">{campaign.name}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {campaign.description}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={campaign.type}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={statusLabels[campaign.status] || campaign.status}
                                            color={statusColors[campaign.status] || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            icon={<QueueIcon />}
                                            label={campaign.queueId?.name || 'Kuyruk Yok'}
                                            size="small"
                                            variant="outlined"
                                            color={campaign.queueId ? 'primary' : 'error'}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        {campaign.statistics?.totalContacts || 0}
                                    </TableCell>
                                    <TableCell align="center">
                                        {campaign.statistics?.answeredCalls || 0} / {campaign.statistics?.totalCalls || 0}
                                    </TableCell>
                                    <TableCell align="center">
                                        {campaign.statistics?.totalCalls
                                            ? Math.round((campaign.statistics.answeredCalls / campaign.statistics.totalCalls) * 100)
                                            : 0}%
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                            {/* Control buttons based on status */}
                                            {campaign.status === 'draft' && (
                                                <Tooltip title="Başlat">
                                                    <IconButton size="small" color="success" onClick={() => handleStart(campaign)}>
                                                        <PlayIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {campaign.status === 'running' && (
                                                <Tooltip title="Duraklat">
                                                    <IconButton size="small" color="warning" onClick={() => handlePause(campaign)}>
                                                        <PauseIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {campaign.status === 'paused' && (
                                                <>
                                                    <Tooltip title="Devam Et">
                                                        <IconButton size="small" color="success" onClick={() => handleResume(campaign)}>
                                                            <PlayIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Durdur">
                                                        <IconButton size="small" color="error" onClick={() => handleStop(campaign)}>
                                                            <StopIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                            {campaign.status === 'running' && (
                                                <Tooltip title="Durdur">
                                                    <IconButton size="small" color="error" onClick={() => handleStop(campaign)}>
                                                        <StopIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            
                                            <Tooltip title="Detaylar">
                                                <IconButton size="small" onClick={() => openDetailDialog(campaign)}>
                                                    <StatsIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Düzenle">
                                                <IconButton size="small" onClick={() => openEditDialog(campaign)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Klonla">
                                                <IconButton size="small" onClick={() => handleClone(campaign)}>
                                                    <CloneIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Sil">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                        setSelectedCampaign(campaign);
                                                        setDeleteConfirmOpen(true);
                                                    }}
                                                    disabled={campaign.status === 'running'}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {campaigns.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography color="textSecondary" sx={{ py: 4 }}>
                                            Henüz kampanya yok. Yeni kampanya oluşturmak için butona tıklayın.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            
            {/* Create Dialog */}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Yeni Kampanya Oluştur</DialogTitle>
                <DialogContent>
                    {renderCampaignForm()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>İptal</Button>
                    <Button variant="contained" onClick={handleCreate} disabled={!formData.name}>
                        Oluştur
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Kampanya Düzenle</DialogTitle>
                <DialogContent>
                    {renderCampaignForm()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>İptal</Button>
                    <Button variant="contained" onClick={handleUpdate}>
                        Kaydet
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Detail Dialog */}
            <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Kampanya Detayları - {selectedCampaign?.name}</DialogTitle>
                <DialogContent>
                    {/* Queue Info */}
                    {selectedCampaign?.queueId && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Kuyruk:</strong> {selectedCampaign.queueId.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Agent atamaları kuyruk üzerinden yapılır. Kuyruk yönetiminden agent ekleyebilirsiniz.
                            </Typography>
                        </Alert>
                    )}
                    
                    {campaignStatus && (
                        <Grid container spacing={2}>
                            {/* Realtime Stats */}
                            {campaignStatus.realtime && (
                                <>
                                    <Grid size={{ xs: 12 }}>
                                        <Typography variant="h6" gutterBottom>
                                            <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                            Gerçek Zamanlı
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <Card>
                                            <CardContent>
                                                <Typography color="textSecondary" gutterBottom>
                                                    Aktif Çağrı
                                                </Typography>
                                                <Typography variant="h4">
                                                    {campaignStatus.realtime.activeCallCount}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <Card>
                                            <CardContent>
                                                <Typography color="textSecondary" gutterBottom>
                                                    Müsait Agent
                                                </Typography>
                                                <Typography variant="h4">
                                                    {campaignStatus.realtime.availableAgentCount}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <Card>
                                            <CardContent>
                                                <Typography color="textSecondary" gutterBottom>
                                                    Dial Ratio
                                                </Typography>
                                                <Typography variant="h4">
                                                    {campaignStatus.realtime.currentDialRatio.toFixed(1)}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid size={{ xs: 6, sm: 3 }}>
                                        <Card>
                                            <CardContent>
                                                <Typography color="textSecondary" gutterBottom>
                                                    Cevaplama %
                                                </Typography>
                                                <Typography variant="h4">
                                                    {Math.round(campaignStatus.realtime.answerRate * 100)}%
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </>
                            )}
                            
                            {/* Contact Stats */}
                            <Grid size={{ xs: 12 }}>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    <ListIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                    Kişi Durumları
                                </Typography>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    {Object.entries(campaignStatus.contacts).map(([status, count]) => (
                                        <Chip
                                            key={status}
                                            label={`${status}: ${count}`}
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            </Grid>
                            
                            {/* Lists */}
                            <Grid size={{ xs: 12 }}>
                                <Divider sx={{ my: 2 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6">
                                        Listeler ({campaignLists.length})
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={() => setCsvDialogOpen(true)}
                                        size="small"
                                    >
                                        CSV Yükle
                                    </Button>
                                </Box>
                            </Grid>
                            {campaignLists.length > 0 ? (
                                <Grid size={{ xs: 12 }}>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Liste Adı</TableCell>
                                                    <TableCell>Kaynak</TableCell>
                                                    <TableCell align="right">Toplam</TableCell>
                                                    <TableCell align="right">Bekleyen</TableCell>
                                                    <TableCell align="right">Tamamlanan</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {campaignLists.map((list) => (
                                                    <TableRow key={list._id}>
                                                        <TableCell>{list.name}</TableCell>
                                                        <TableCell>{list.sourceType}</TableCell>
                                                        <TableCell align="right">{list.statistics.totalContacts}</TableCell>
                                                        <TableCell align="right">{list.statistics.pendingContacts}</TableCell>
                                                        <TableCell align="right">{list.statistics.completedContacts}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            ) : (
                                <Grid size={{ xs: 12 }}>
                                    <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#f8fafc' }}>
                                        <ListIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                                        <Typography color="textSecondary" gutterBottom>
                                            Henüz liste eklenmemiş
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                            Kampanyayı başlatmak için en az bir liste eklemelisiniz
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={() => setCsvDialogOpen(true)}
                                        >
                                            CSV Yükle
                                        </Button>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailDialogOpen(false)}>Kapat</Button>
                </DialogActions>
            </Dialog>
            
            {/* Delete Confirm Dialog */}
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                <DialogTitle>Kampanya Sil</DialogTitle>
                <DialogContent>
                    <Typography>
                        "{selectedCampaign?.name}" kampanyasını silmek istediğinize emin misiniz?
                        Bu işlem geri alınamaz.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>İptal</Button>
                    <Button variant="contained" color="error" onClick={handleDelete}>
                        Sil
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* CSV Import Dialog */}
            <Dialog open={csvDialogOpen} onClose={resetCsvDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    CSV Dosyası Yükle
                    {csvStep === 'mapping' && ' - Sütun Eşleştirme'}
                </DialogTitle>
                <DialogContent>
                    {csvStep === 'upload' && (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                            <input
                                accept=".csv"
                                style={{ display: 'none' }}
                                id="csv-file-input"
                                type="file"
                                onChange={handleCsvFileSelect}
                            />
                            <label htmlFor="csv-file-input">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    size="large"
                                    sx={{ py: 3, px: 6 }}
                                >
                                    <Box>
                                        <AddIcon sx={{ fontSize: 48, mb: 1 }} />
                                        <Typography>CSV Dosyası Seç</Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            Telefon numarası sütunu zorunludur
                                        </Typography>
                                    </Box>
                                </Button>
                            </label>
                            
                            <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>Örnek CSV Formatı:</Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre' }}>
{`phoneNumber,firstName,lastName,company,email
5551234567,Ahmet,Yılmaz,ABC Ltd,ahmet@abc.com
5559876543,Mehmet,Demir,XYZ AŞ,mehmet@xyz.com`}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    
                    {csvStep === 'mapping' && (
                        <Box>
                            <TextField
                                fullWidth
                                label="Liste Adı"
                                value={csvListName}
                                onChange={(e) => setCsvListName(e.target.value)}
                                sx={{ mb: 3 }}
                            />
                            
                            <Typography variant="subtitle1" gutterBottom>
                                Sütun Eşleştirme
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                CSV sütunlarını sistem alanlarıyla eşleştirin. Telefon numarası zorunludur.
                            </Typography>
                            
                            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>CSV Sütunu</TableCell>
                                            <TableCell>Sistem Alanı</TableCell>
                                            <TableCell>Örnek Veri</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {csvHeaders.map((header, index) => (
                                            <TableRow key={header}>
                                                <TableCell>{header}</TableCell>
                                                <TableCell>
                                                    <FormControl size="small" fullWidth>
                                                        <Select
                                                            value={csvMapping[header] || ''}
                                                            onChange={(e) => setCsvMapping(prev => ({
                                                                ...prev,
                                                                [header]: e.target.value
                                                            }))}
                                                            displayEmpty
                                                        >
                                                            <MenuItem value="">
                                                                <em>Eşleştirme yok</em>
                                                            </MenuItem>
                                                            <MenuItem value="phoneNumber">📱 Telefon Numarası *</MenuItem>
                                                            <MenuItem value="firstName">👤 Ad</MenuItem>
                                                            <MenuItem value="lastName">👤 Soyad</MenuItem>
                                                            <MenuItem value="company">🏢 Firma</MenuItem>
                                                            <MenuItem value="email">✉️ E-posta</MenuItem>
                                                            <MenuItem value="alternatePhone">📞 Alternatif Telefon</MenuItem>
                                                            <MenuItem value="notes">📝 Notlar</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell sx={{ color: 'text.secondary', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {csvPreview[0]?.[index] || '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            
                            <Typography variant="subtitle2" gutterBottom>
                                Önizleme ({csvPreview.length} satır)
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            {csvHeaders.map(h => (
                                                <TableCell key={h}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {csvPreview.map((row, idx) => (
                                            <TableRow key={idx}>
                                                {row.map((cell, cellIdx) => (
                                                    <TableCell key={cellIdx}>{cell}</TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            
                            {!csvMapping.phoneNumber && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    Telefon numarası sütunu eşleştirilmedi. Bu alan zorunludur.
                                </Alert>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={resetCsvDialog}>İptal</Button>
                    {csvStep === 'mapping' && (
                        <Button
                            variant="contained"
                            onClick={handleCsvUpload}
                            disabled={csvUploading || !csvMapping.phoneNumber}
                            startIcon={csvUploading ? <CircularProgress size={20} /> : <AddIcon />}
                        >
                            {csvUploading ? 'Yükleniyor...' : 'Listeyi Yükle'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}
